import type { ToastServiceMethods } from "primevue/toastservice"

import { apolloClient } from "../queries/client"
import DESTROY_DOCUMENT from "../queries/document/destroy.gql"
import REORDER_DOCUMENTS from "../queries/document/reorder.gql"
import UPDATE_DOCUMENT from "../queries/document/update.gql"

export interface MutationResult {
  success: boolean
  errors?: string[]
}

export function useDocumentMutations(toast: ToastServiceMethods) {
  const updateDocument = async (
    documentId: string,
    name: string,
  ): Promise<MutationResult> => {
    try {
      const result = await apolloClient.mutate({
        mutation: UPDATE_DOCUMENT,
        variables: { documentId, name },
      })

      if (result?.data?.updateDocument?.errors?.length) {
        return { success: false, errors: result.data.updateDocument.errors }
      }

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Document updated successfully",
        life: 4000,
      })

      return { success: true }
    } catch (error) {
      console.error("Mutation error:", error)
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to update document",
        life: 5000,
      })
      return { success: false }
    }
  }

  const destroyDocument = async (
    documentId: string,
  ): Promise<MutationResult> => {
    try {
      const result = await apolloClient.mutate({
        mutation: DESTROY_DOCUMENT,
        variables: { documentId },
      })

      if (result?.data?.destroyDocument?.success) {
        toast.add({
          severity: "success",
          summary: "Success",
          detail: "Document deleted",
          life: 4000,
        })
        return { success: true }
      } else {
        const errors = result?.data?.destroyDocument?.errors?.length
          ? result.data.destroyDocument.errors
          : ["Unknown error"]
        toast.add({
          severity: "error",
          summary: "Error",
          detail: errors.join(", "),
          life: 5000,
        })
        return { success: false, errors }
      }
    } catch (error) {
      console.error("Mutation error:", error)
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete document",
        life: 5000,
      })
      return { success: false }
    }
  }

  const reorderDocuments = async (
    orderId: string,
    documentIds: string[],
  ): Promise<MutationResult> => {
    try {
      const result = await apolloClient.mutate({
        mutation: REORDER_DOCUMENTS,
        variables: { orderId, documentIds },
      })

      if (result?.data?.reorderDocuments?.success) {
        return { success: true }
      } else {
        const errors = result?.data?.reorderDocuments?.errors?.length
          ? result.data.reorderDocuments.errors
          : ["Unknown error"]
        toast.add({
          severity: "error",
          summary: "Error",
          detail: errors.join(", "),
          life: 5000,
        })
        return { success: false, errors }
      }
    } catch (error) {
      console.error("Mutation error:", error)
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to reorder documents",
        life: 5000,
      })
      return { success: false }
    }
  }

  return { updateDocument, destroyDocument, reorderDocuments }
}
